import type { Order, Pharmacy, Entity } from "../lib/types";

// Add new interfaces for dashboard statistics
interface OrderStats {
	status: string;
	count: string;
}

interface PharmacyStats {
	pharmacy: string;
	"1D": number;
	"2D": number;
	"5D+": number;
	// Additional fields for Hyperlocal mode
	"10 mins"?: number;
	"11-20 mins"?: number;
	"21-30 mins"?: number;
	"31-40 mins"?: number;
	"41-50 mins"?: number;
	"51-60 mins"?: number;
}

interface StatusStats {
	status: string;
	"1D": number;
	"2D": number;
	total_orders: number;
}

interface OrderFilters {
	group_by?: string;
	start_date?: string;
	end_date?: string;
	city?: string;
	provider?: string;
	mode?: string;
}

const mockPharmacies: Pharmacy[] = [
	{
		id: "1",
		name: "Central Pharmacy",
		location: {
			latitude: 40.7128,
			longitude: -74.006,
		},
		fulfillmentPercentage: 95,
		currentBills: 50,
		maxBills: 100,
		fulfillmentSpeed: 85,
		remainingOperatingHours: 8,
		totalOperatingHours: 12,
		slaAdherence: 98,
		inventory: {
			Aspirin: 100,
			Bandages: 200,
			Antibiotics: 50,
			"Cough Syrup": 75,
			Paracetamol: 150,
			"Allergy Medicine": 30,
		},
		procurementTime: 4,
		distance: 0,
	},
	{
		id: "2",
		name: "Westside Drugs",
		location: {
			latitude: 40.7589,
			longitude: -73.9851,
		},
		fulfillmentPercentage: 88,
		currentBills: 40,
		maxBills: 80,
		fulfillmentSpeed: 80,
		remainingOperatingHours: 6,
		totalOperatingHours: 10,
		slaAdherence: 95,
		inventory: {
			Aspirin: 75,
			Bandages: 150,
			Antibiotics: 25,
			"Cough Syrup": 100,
			Paracetamol: 200,
			"Allergy Medicine": 10,
		},
		procurementTime: 6,
		distance: 0,
	},
	{
		id: "3",
		name: "Eastside Meds",
		location: {
			latitude: 40.7549,
			longitude: -73.984,
		},
		fulfillmentPercentage: 92,
		currentBills: 45,
		maxBills: 90,
		fulfillmentSpeed: 82,
		remainingOperatingHours: 7,
		totalOperatingHours: 11,
		slaAdherence: 97,
		inventory: {
			Aspirin: 90,
			Bandages: 180,
			Antibiotics: 40,
			"Cough Syrup": 60,
			Paracetamol: 100,
			"Allergy Medicine": 50,
		},
		procurementTime: 5,
		distance: 0,
	},
];

// Add mock data for order statistics
const mockOrderStats: OrderStats[] = [
	{ status: "Pending", count: "45" },
	{ status: "Accepted", count: "30" },
	{ status: "Dispatched", count: "25" },
	{ status: "Delivered", count: "80" },
	{ status: "Rejected", count: "10" },
	// { status: "Billing", count: "30" },
	// { status: "Billed", count: "25" },
	// { status: "Transferred", count: "80" },
	// { status: "RTO", count: "10" },
];

// Update mockPharmacyStats with city and provider information
const mockPharmacyStats: (PharmacyStats & {
	city: string;
	provider: string;
})[] = [
	{
		pharmacy: "MediSYS",
		city: "hyderabad",
		provider: "medibuddy",
		"1D": 30,
		"2D": 20,
		"5D+": 15,
		"10 mins": 10,
		"11-20 mins": 15,
		"21-30 mins": 20,
		"31-40 mins": 12,
		"41-50 mins": 8,
		"51-60 mins": 5,
	},
	{
		pharmacy: "FCP",
		city: "chennai",
		provider: "pazcare",
		"1D": 20,
		"2D": 15,
		"5D+": 10,
		"10 mins": 8,
		"11-20 mins": 12,
		"21-30 mins": 15,
		"31-40 mins": 10,
		"41-50 mins": 5,
		"51-60 mins": 3,
	},
	{
		pharmacy: "SSP",
		city: "kolkata",
		provider: "flipkart",
		"1D": 35,
		"2D": 20,
		"5D+": 25,
		"10 mins": 12,
		"11-20 mins": 18,
		"21-30 mins": 25,
		"31-40 mins": 15,
		"41-50 mins": 10,
		"51-60 mins": 7,
	},
];

const mockStatusStats: StatusStats[] = [
	{ status: "Pending", "1D": 20, "2D": 15, total_orders: 45 },
	{ status: "Accepted", "1D": 15, "2D": 10, total_orders: 30 },
	{ status: "Dispatched", "1D": 12, "2D": 8, total_orders: 25 },
	{ status: "Delivered", "1D": 40, "2D": 30, total_orders: 80 },
	{ status: "Rejected", "1D": 5, "2D": 3, total_orders: 10 },
];

export const mockDataService = {
	getPharmacies: (): Promise<Pharmacy[]> => {
		return Promise.resolve(mockPharmacies);
	},

	getOrders: async (): Promise<Order[]> => {
		return [
			// ... mock order data
		];
	},

	getPharmacyById: async (id: string): Promise<Pharmacy | undefined> => {
		const pharmacies = await mockDataService.getPharmacies();
		return pharmacies.find((p) => p.id === id);
	},

	calculateDistance: (
		lat1: number,
		lon1: number,
		lat2: number,
		lon2: number,
	): number => {
		// Convert degrees to radians
		const toRad = (x: number): number => (x * Math.PI) / 180;

		const R = 6371; // Earth's radius in km
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);

		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat1)) *
				Math.cos(toRad(lat2)) *
				Math.sin(dLon / 2) *
				Math.sin(dLon / 2);

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		// Round to 1 decimal place
		return Math.round(R * c * 10) / 10;
	},

	getGroupedOrders: async (filters: OrderFilters): Promise<OrderStats[]> => {
		// Apply filters to mock data
		let filteredStats = [...mockOrderStats];

		if (filters.city || filters.provider || filters.mode) {
			// Simulate filtering by reducing counts
			filteredStats = filteredStats.map((stat) => ({
				...stat,
				count: Math.floor(Number.parseInt(stat.count) * 0.7).toString(),
			}));
		}

		return filteredStats;
	},

	getPharmacyStats: async (filters: OrderFilters): Promise<PharmacyStats[]> => {
		let filteredStats = [...mockPharmacyStats];

		if (filters.city && filters.city !== "all") {
			filteredStats = filteredStats.filter(
				(stat) => stat.city === filters.city,
			);
		}

		if (filters.provider && filters.provider !== "all") {
			filteredStats = filteredStats.filter(
				(stat) => stat.provider === filters.provider,
			);
		}

		// Remove the extra properties before returning
		return filteredStats.map(({ city, provider, ...rest }) => rest);
	},

	getStatusStats: async (filters: OrderFilters): Promise<StatusStats[]> => {
		let filteredStats = [...mockStatusStats];

		if (filters.city || filters.provider || filters.mode) {
			// Simulate filtering by reducing counts
			filteredStats = filteredStats.map((stat) => ({
				...stat,
				"1D": Math.floor(stat["1D"] * 0.7),
				"2D": Math.floor(stat["2D"] * 0.7),
				total_orders: Math.floor(stat.total_orders * 0.7),
			}));
		}

		return filteredStats;
	},
};

export const COMMANDS = [
	{
		id: "today-orders",
		name: "Show today's orders",
		shortcut: ["1"],
		action: (navigate: (path: string) => void) => {
			const today = new Date().toISOString().split("T")[0];
			navigate(`/orders?startDate=${today}&endDate=${today}`);
		},
	},
	{
		id: "yesterday-orders",
		name: "Show yesterday's orders",
		shortcut: ["Y"],
		action: (navigate: (path: string) => void) => {
			const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split("T")[0];
			navigate(`/orders?startDate=${yesterday}&endDate=${yesterday}`);
		},
	},
	{
		id: "week-orders",
		name: "Show last 7 days orders",
		shortcut: ["7"],
		action: (navigate: (path: string) => void) => {
			const end = new Date().toISOString().split("T")[0];
			const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
				.toISOString()
				.split("T")[0];
			navigate(`/orders?startDate=${start}&endDate=${end}`);
		},
	},
	{
		id: "fifteen-days-orders",
		name: "Show last 15 days orders",
		shortcut: ["15"],
		action: (navigate: (path: string) => void) => {
			const end = new Date().toISOString().split("T")[0];
			const start = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)
				.toISOString()
				.split("T")[0];
			navigate(`/orders?startDate=${start}&endDate=${end}`);
		},
	},
	{
		id: "month-orders",
		name: "Show last month orders",
		shortcut: ["M"],
		action: (navigate: (path: string) => void) => {
			const end = new Date().toISOString().split("T")[0];
			const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
				.toISOString()
				.split("T")[0];
			navigate(`/orders?startDate=${start}&endDate=${end}`);
		},
	},
	{
		id: "cancelled-orders",
		name: "Show Cancelled Orders",
		shortcut: ["C"],
		action: (navigate: (path: string) => void) => {
			navigate("/orders?status=Cancelled");
		},
	},
	{
		id: "cancelled-orders-last-n-days",
		name: "Show Cancelled Orders in Last N Days",
		shortcut: ["CN"],
		action: (navigate: (path: string) => void) => {
			const days = prompt("Enter number of days (e.g., 7):");
			if (days && !isNaN(Number(days))) {
				const end = new Date().toISOString().split("T")[0];
				const start = new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000)
					.toISOString()
					.split("T")[0];
				navigate(`/orders?startDate=${start}&endDate=${end}&status=Cancelled`);
			}
		},
	},
];

export const PHARMACY_COMMANDS = [
	{
		id: "medisys-orders",
		name: "MediSYS Orders",
		shortcut: ["MS"],
		action: (navigate: (path: string) => void) => {
			navigate("/orders?entity=5");
		},
	},
	{
		id: "new-devi-orders",
		name: "New Devi Orders",
		shortcut: ["ND"],
		action: (navigate: (path: string) => void) => {
			navigate("/orders?entity=17");
		},
	},
	{
		id: "ravi-pharmacy-orders",
		name: "Ravi Pharmacy Orders",
		shortcut: ["RP"],
		action: (navigate: (path: string) => void) => {
			navigate("/orders?entity=1");
		},
	},
];

// For dynamic pharmacy commands
export const generateDynamicPharmacyCommands = (entities: Entity[]) => {
	const activePharmacies = entities.filter(
		entity => entity.type === "pharmacy" && entity.is_active === true
	);

	return activePharmacies.map(pharmacy => ({
		id: `${pharmacy.name?.toLowerCase().replace(/\s+/g, "-")}-orders`,
		name: `${pharmacy.name} Orders`,
		shortcut: [pharmacy.name?.split(" ").map((word: string) => word.charAt(0).toUpperCase()).join("") || ""],
		action: (navigate: (path: string) => void) => {
			navigate(`/orders?entity=${pharmacy.id}`);
		},
	}));
};

export const COMING_SOON_COMMANDS = [
	{
		id: "track-order",
		name: "Track an order",
		shortcut: ["T"],
		action: (navigate: (path: string) => void) => {
			console.log("Track order - Coming Soon");
		},
	},
	{
		id: "update-order",
		name: "Update an order",
		shortcut: ["U"],
		action: (navigate: (path: string) => void) => {
			console.log("Update order - Coming Soon");
		},
	},
	{
		id: "update-awb",
		name: "Update AWB",
		shortcut: ["A"],
		action: (navigate: (path: string) => void) => {
			console.log("Update AWB - Coming Soon");
		},
	},
];

export const cities = [
	{ value: "all", label: "All Cities" },
	{ value: "hyderabad", label: "Hyderabad" },
	{ value: "chennai", label: "Chennai" },
	{ value: "kolkata", label: "Kolkata" },
];

export const providers = [
		{ value: "all", label: "All Providers" },
	{ value: "medibuddy", label: "MediBuddy" },
	{ value: "pazcare", label: "PazCare" },
	{ value: "flipkart", label: "Flipkart Health" },
];


export const modes = [
	{ value: "all", label: "All Modes" },
	{ value: "EXPRESS_DELIVERY", label: "Hyperlocal" },
	{ value: "STANDARD_DELIVERY", label: "Standard Delivery" },
	{ value: "STORE_PICKUP", label: "Store Pickup" },
];


// Mock data service for testing new form fields

export interface MockTag {
  id: number;
  name: string;
}

export interface MockVariantCategory {
  id: number;
  name: string;
  code: string;
}

export interface MockVariantValue {
  id: number;
  value: string;
  code: string;
  category_id: number;
}

// Mock data
const mockTags: MockTag[] = [
  { id: 1, name: 'Prescription' },
  { id: 2, name: 'OTC' },
  { id: 3, name: 'Generic' },
  { id: 4, name: 'Branded' },
  { id: 5, name: 'Chronic' },
  { id: 6, name: 'Acute' },
  { id: 7, name: 'Pediatric' },
  { id: 8, name: 'Geriatric' },
  { id: 9, name: 'High-Risk' },
  { id: 10, name: 'Controlled' },
];

const mockVariantCategories: MockVariantCategory[] = [
  { id: 1, name: 'Color', code: 'COL' },
  { id: 2, name: 'Size', code: 'SIZ' },
  { id: 3, name: 'Flavor', code: 'FLA' },
  { id: 4, name: 'Strength', code: 'STR' },
  { id: 5, name: 'Pack Type', code: 'PAC' },
  { id: 6, name: 'Dosage Form', code: 'DOS' },
];

const mockVariantValues: MockVariantValue[] = [
  // Color variants
  { id: 1, value: 'Red', code: 'RED', category_id: 1 },
  { id: 2, value: 'Blue', code: 'BLU', category_id: 1 },
  { id: 3, value: 'Green', code: 'GRE', category_id: 1 },
  { id: 4, value: 'Yellow', code: 'YEL', category_id: 1 },
  { id: 5, value: 'White', code: 'WHI', category_id: 1 },

  // Size variants
  { id: 6, value: 'Small', code: 'SML', category_id: 2 },
  { id: 7, value: 'Medium', code: 'MED', category_id: 2 },
  { id: 8, value: 'Large', code: 'LAR', category_id: 2 },
  { id: 9, value: 'Extra Large', code: 'XL', category_id: 2 },

  // Flavor variants
  { id: 10, value: 'Mint', code: 'MIN', category_id: 3 },
  { id: 11, value: 'Orange', code: 'ORA', category_id: 3 },
  { id: 12, value: 'Strawberry', code: 'STR', category_id: 3 },
  { id: 13, value: 'Vanilla', code: 'VAN', category_id: 3 },
  { id: 14, value: 'Chocolate', code: 'CHO', category_id: 3 },

  // Strength variants
  { id: 15, value: '10mg', code: '10M', category_id: 4 },
  { id: 16, value: '20mg', code: '20M', category_id: 4 },
  { id: 17, value: '50mg', code: '50M', category_id: 4 },
  { id: 18, value: '100mg', code: '100', category_id: 4 },
  { id: 19, value: '250mg', code: '250', category_id: 4 },

  // Pack Type variants
  { id: 20, value: 'Strip', code: 'STR', category_id: 5 },
  { id: 21, value: 'Bottle', code: 'BOT', category_id: 5 },
  { id: 22, value: 'Vial', code: 'VIA', category_id: 5 },
  { id: 23, value: 'Tube', code: 'TUB', category_id: 5 },
  { id: 24, value: 'Sachet', code: 'SAC', category_id: 5 },

  // Dosage Form variants
  { id: 25, value: 'Tablet', code: 'TAB', category_id: 6 },
  { id: 26, value: 'Capsule', code: 'CAP', category_id: 6 },
  { id: 27, value: 'Syrup', code: 'SYP', category_id: 6 },
  { id: 28, value: 'Injection', code: 'INJ', category_id: 6 },
  { id: 29, value: 'Cream', code: 'CRE', category_id: 6 },
];
